/**
 * Interception du SDK Firestore MODULAIRE (firebase/firestore) pour le mode démo.
 *
 * Le proxy compat (firestoreProxy.js + patch des factories dans conf.js) ne
 * couvre PAS les fichiers qui importent directement `firebase/firestore` :
 * les fonctions modulaires (getDocs, onSnapshot…) déballent le `_delegate`
 * natif de l'instance compat — même quand le fichier utilise initDb().
 *
 * Règle : AUCUN fichier de src/ ne doit importer `firebase/firestore`
 * directement — importer ce module à la place. Il ré-exporte toute l'API
 * modulaire à l'identique et surcharge uniquement :
 * - lectures (getDoc/getDocs/onSnapshot/…): snapshots anonymisés en démo,
 *   PASSTHROUGH natif strict (===) hors démo ;
 * - écritures (setDoc/updateDoc/deleteDoc/addDoc/writeBatch/runTransaction) :
 *   bloquées en démo (lecture seule stricte) ;
 * - curseurs (startAfter/startAt/endAt/endBefore) : déballent les snapshots
 *   proxifiés avant de les passer au SDK.
 */

/* eslint-disable import/export */
import {
  getDoc as _getDoc,
  getDocFromCache as _getDocFromCache,
  getDocFromServer as _getDocFromServer,
  getDocs as _getDocs,
  getDocsFromCache as _getDocsFromCache,
  getDocsFromServer as _getDocsFromServer,
  getCountFromServer as _getCountFromServer,
  onSnapshot as _onSnapshot,
  setDoc as _setDoc,
  updateDoc as _updateDoc,
  deleteDoc as _deleteDoc,
  addDoc as _addDoc,
  writeBatch as _writeBatch,
  runTransaction as _runTransaction,
  startAfter as _startAfter,
  startAt as _startAt,
  endAt as _endAt,
  endBefore as _endBefore,
} from "firebase/firestore";

import { isDemoActive } from "./demoState";
import { createDemoWriteBlockedError } from "./demoWriteBlock";
import { wrapSnapshot, unwrapDemoSnapshot } from "./firestoreProxy";

export * from "firebase/firestore";

function rejectWrite(method) {
  const message = `Mode démo actif : écriture Firestore bloquée (${method}). / Demo mode: writes are disabled.`;
  console.warn(message);
  return Promise.reject(createDemoWriteBlockedError(message));
}

// Les refs/snapshots reçus peuvent être des proxies démo : toujours déballer
// avant de les donner au SDK natif.
const unwrapArgs = (args) => args.map(unwrapDemoSnapshot);

function wrapRead(fn) {
  return async (...args) => {
    const snap = await fn(...unwrapArgs(args));
    return isDemoActive() ? wrapSnapshot(snap) : snap;
  };
}

export const getDoc = wrapRead(_getDoc);
export const getDocFromCache = wrapRead(_getDocFromCache);
export const getDocFromServer = wrapRead(_getDocFromServer);
export const getDocs = wrapRead(_getDocs);
export const getDocsFromCache = wrapRead(_getDocsFromCache);
export const getDocsFromServer = wrapRead(_getDocsFromServer);
export const getCountFromServer = (...args) =>
  _getCountFromServer(...unwrapArgs(args));

/** onSnapshot(ref, onNext | observer, ...) — flag relu à CHAQUE émission. */
export function onSnapshot(ref, ...args) {
  let onNextWrapped = false;
  const mapped = args.map((arg) => {
    if (!onNextWrapped && typeof arg === "function") {
      onNextWrapped = true;
      return (snap, ...rest) => arg(isDemoActive() ? wrapSnapshot(snap) : snap, ...rest);
    }
    if (!onNextWrapped && arg && typeof arg === "object" && typeof arg.next === "function") {
      onNextWrapped = true;
      return { ...arg, next: (snap) => arg.next(isDemoActive() ? wrapSnapshot(snap) : snap) };
    }
    return arg;
  });
  return _onSnapshot(unwrapDemoSnapshot(ref), ...mapped);
}

export const setDoc = (...args) =>
  isDemoActive() ? rejectWrite("setDoc") : _setDoc(...unwrapArgs(args));
export const updateDoc = (...args) =>
  isDemoActive() ? rejectWrite("updateDoc") : _updateDoc(...unwrapArgs(args));
export const deleteDoc = (...args) =>
  isDemoActive() ? rejectWrite("deleteDoc") : _deleteDoc(...unwrapArgs(args));
export const addDoc = (...args) =>
  isDemoActive() ? rejectWrite("addDoc") : _addDoc(...unwrapArgs(args));
export const runTransaction = (...args) =>
  isDemoActive() ? rejectWrite("runTransaction") : _runTransaction(...args);

/** Démo OFF à la création : batch natif brut. En démo : commit bloqué. */
export function writeBatch(...args) {
  const batch = _writeBatch(...args);
  if (!isDemoActive()) return batch;
  return new Proxy(batch, {
    get(target, prop) {
      if (prop === "commit") {
        return (...commitArgs) =>
          isDemoActive() ? rejectWrite("batch.commit") : target.commit(...commitArgs);
      }
      const value = target[prop];
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

export const startAfter = (...args) => _startAfter(...unwrapArgs(args));
export const startAt = (...args) => _startAt(...unwrapArgs(args));
export const endAt = (...args) => _endAt(...unwrapArgs(args));
export const endBefore = (...args) => _endBefore(...unwrapArgs(args));
